import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import useAuthSuccess from './useAuthSuccess';
import { getSocialProviders, startSocialLogin, finishSocialLogin } from '../../api_social_auth';
import './socialAuth.css';

const KEY = 'lovirds.social-login.v1';
const read = () => { try { return JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch { return null; } };
const forget = () => { try { sessionStorage.removeItem(KEY); } catch {} };

export default function SocialAuthButtons() {
    const navigate = useNavigate();
    const applyAuth = useAuthSuccess();
    const onAuth = useRef(applyAuth);
    onAuth.current = applyAuth;
    const [providers, setProviders] = useState({});
    const [attempt, setAttempt] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const starting = useRef(false);

    useEffect(() => {
        let alive = true;
        getSocialProviders().then(data => { if (alive) setProviders(data); }).catch(() => {});
        const hash = new URLSearchParams(window.location.hash.slice(1));
        let saved = read();
        if (hash.has('social_error')) {
            forget(); saved = null; setError('Вход не завершён. Попробуйте ещё раз.');
        } else if (hash.get('social') === 'vk') {
            if (saved?.attempt === hash.get('attempt') && saved.provider === 'vk' && hash.get('handoff')) {
                saved.handoff = hash.get('handoff');
                try { sessionStorage.setItem(KEY, JSON.stringify(saved)); } catch { saved = null; }
            } else {
                saved = null; setError('Для завершения входа откройте его в том же браузере, где начали.');
            }
        }
        if (hash.has('social') || hash.has('social_error')) {
            window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
        }
        if (saved && Date.parse(saved.expires_at) > Date.now() && (saved.provider === 'max' || saved.handoff)) setAttempt(saved);
        else forget();
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (!attempt) return undefined;
        let active = true, timer;
        const poll = async () => {
            if (!active) return;
            if (Date.parse(attempt.expires_at) <= Date.now()) {
                forget(); setAttempt(null); setError('Время входа истекло. Нажмите кнопку ещё раз.'); return;
            }
            try {
                const result = await finishSocialLogin(attempt);
                if (!active) return;
                if (result.token) {
                    forget(); setAttempt(null); onAuth.current(result, result.platform); navigate('/', { replace: true }); return;
                }
            } catch (e) {
                if (!active) return;
                if (e.response && e.response.status !== 429 && e.response.status < 500) {
                    forget(); setAttempt(null); setError(e.response.data?.error || 'Не удалось завершить вход'); return;
                }
            }
            if (active) timer = setTimeout(poll, 3000);
        };
        poll();
        return () => { active = false; clearTimeout(timer); };
    }, [attempt, navigate]);

    const start = async provider => {
        if (starting.current) return;
        starting.current = true; setBusy(true); setError('');
        const tab = provider === 'max' ? window.open('about:blank', '_blank') : null;
        if (tab) tab.opener = null;
        try {
            sessionStorage.setItem(KEY, 'null');
            const result = await startSocialLogin(provider);
            const saved = { ...result, provider };
            sessionStorage.setItem(KEY, JSON.stringify(saved));
            if (provider === 'vk') window.location.assign(result.url);
            else { setAttempt(saved); if (tab) tab.location.replace(result.url); }
        } catch (e) {
            if (tab) tab.close();
            setError(e.response?.data?.error || 'Не удалось начать вход. Проверьте интернет и разрешение на хранение данных сайта.');
        } finally { starting.current = false; setBusy(false); }
    };

    if (!providers.vk && !providers.max && !attempt && !error) return null;
    return <section className="lv-social" aria-label="Вход через мессенджеры">
        {error && <p className="lv-social__error" role="alert">{error}</p>}
        {attempt ? <div className="lv-social__pending" aria-live="polite">
            <p>{attempt.provider === 'max' ? 'Подтвердите вход в MAX и вернитесь в этот браузер' : 'Завершаем вход через VK…'}</p>
            {attempt.provider === 'max' && <>
                <strong className="lv-social__code">{attempt.display_code}</strong>
                <p className="lv-social__hint">Код в боте должен совпадать</p>
                <a className="lv-social__button" href={attempt.url} target="_blank" rel="noopener noreferrer">Открыть MAX</a>
                <details><summary>Входите с компьютера?</summary><p>Откройте ссылку на телефоне, подтвердите код и вернитесь сюда:</p><a className="lv-social__url" href={attempt.url} target="_blank" rel="noopener noreferrer">{attempt.url}</a><p>Если бот не показал подтверждение, отправьте ему:</p><code className="lv-social__url">/start lv_{attempt.attempt.replace(/-/g, '')}</code></details>
            </>}
            <button type="button" className="lv-social__cancel" onClick={() => { forget(); setAttempt(null); }}>Отменить</button>
        </div> : <>
            <div className="lv-social__divider">или через мессенджер</div>
            <div className="lv-social__buttons">
                {providers.vk && <button type="button" className="lv-social__button lv-social__button--vk" disabled={busy} onClick={() => start('vk')}>Войти через VK</button>}
                {providers.max && <button type="button" className="lv-social__button lv-social__button--max" disabled={busy} onClick={() => start('max')}>Войти через MAX</button>}
            </div>
        </>}
    </section>;
}
